import { measure } from "./performance";

/**
 * The typed client. It knows endpoint names and response shapes and NOTHING
 * about SQL — spec principle 3, and the reason this file is boring.
 *
 * There is no place here to pass a fragment, a sort column, or a table name.
 * If a screen needs a number the catalog does not expose, the catalog gains an
 * entry; the client never gains an escape hatch.
 */

export const BASE: string =
  import.meta.env["VITE_API_URL"] ?? "http://localhost:8080";

export interface DisplayEntry {
  name: string;
  description?: string;
}

export interface CurrencyMetadata extends DisplayEntry {
  symbol: string;
  decimals: number;
  icon?: string;
}

export interface EventMetadata extends DisplayEntry {
  owner?: string;
  expectedProperties: string[];
}

export interface FunnelMetadata extends DisplayEntry {
  steps: Record<string, string>;
}

export interface JourneyDefinition {
  schemaVersion: 1; id: string; version: number; name: string;
  entry: string; completion: string; observationHours: number; historyFrom: string;
  nodes: { id: string; name: string; sources: { event: string; funnel: string; step: number; stepName: string; builds: string[] }[] }[];
  edges: { from: string; to: string }[];
  branches: { id: string; name: string; choice: string }[];
}
export interface ProductDashboardPanel {
  id: string; title: string; group: string; note: string; needs: string[]; unit: "count" | "percent" | "seconds" | "robux" | "ratio";
  summary?: string; countingRules?: string;
  sampleLabel: string;
  details?: Record<string, "count" | "percent" | "seconds" | "robux" | "ratio">; money?: boolean; restricted: boolean;
}
export interface ProductDashboardRow { label: string; series: string; value: number | null; sample: number; details: Record<string, number | null> }
// Players online per interval by type (first day / first seen 1-30 days ago / back after 30+ days away / regulars), averages.
export interface CcuTypePoint { at: string; firstDay: number; recent: number; lapsed: number; regular: number; peak: number }
// Exit rates by device for the Sessions page (null rates where a window has no visits).
export interface DeviceExitPoint { at: string; sessions: number; under60: number | null; under180: number | null }
export interface DeviceExits { windowHours: number; bucketMinutes: number; rateWindowMinutes: number; available: boolean;
  devices: { mobile: DeviceExitPoint[]; pc: DeviceExitPoint[]; console: DeviceExitPoint[] } | null;
  totals?: Record<"mobile" | "pc" | "console", { sessions: number; under60: number | null; under180: number | null }> | null }
export interface NotificationRow { day: string; stage: string; kind: string; outcome: string; surface: string; events: number; players: number; last_ms: number }
export interface NotificationReport {
  campaigns: { id: string; title: string }[]; campaign: string; title: string; environment: string;
  stages: { id: string; label: string }[]; surfaces: string[]; surfaceLabels?: Record<string, string>; rows: NotificationRow[];
  runs: { run_id: string; at_ms: number; props: Record<string, string> }[]; runsLimited: boolean;
  performance?: NotificationPerformanceRow[]; cohorts?: NotificationCohortRow[]; testStart?: string;
  // Parts that ran past the database budget this time ("performance", "cohorts"); their sections are empty until a refetch.
  incomplete?: string[];
}
// Per finished UTC day and reminder stage: players whose day N it was (`due`), and how many played that day (`back`), in four
// groups that add up to `due`: sent the reminder that day; refused because notifications are off; asked but no attempt that
// day (mostly back and claimed before it went out); did not ask.
export interface NotificationCohortRow { day: string; stage: string; due: number; back: number; sent: number; sent_back: number; off: number; off_back: number; asked_other: number; asked_other_back: number; not_asked: number; not_asked_back: number }
// Sends per reminder (grp "stage", key = stage id), overall (grp "all") and per half of the send-time test (grp "half", key
// "own_hour" | "fixed"). `complete` are sends at least 24 hours old; `returned`, `same_day` and `tapped` count only those.
export interface NotificationPerformanceRow { grp: string; key: string; pushes: number; players: number; complete: number; returned: number; same_day: number; tapped: number }
export interface JourneyRegistration {
  definition: JourneyDefinition; definitionHash: string;
  status: "conflict" | "backfilling" | "updating" | "ready";
  coverage: { historyFrom: string | null; historyThrough: string | null; registeredAt: string | null; updatedAt: string | null; pendingInputHours: number };
}
export interface JourneyDetail extends JourneyRegistration {
  selection?: JourneySelection;
  flow?: {
    basis: "ordered-observations";
    edges: { from: string; to: string; players: number }[];
    nodes: { id: string; noNext: number; timeRemaining?: number; sessionEnded?: number; unclear?: number; multipleNext: number; incoming: { parents: string[]; players: number }[] }[];
  };
  retention?: { from: string; to: string; observedThrough: string; basis: string; progressDay: number; returnDay: number };
  countingUnit: "player"; hours: number; entrants: number; completed: number;
  mature: number; matureCompleted: number; changedPath: number; unknownChoice: number;
  pendingChoice: number; closedNoChoice: number; choiceConflicts: number;
  excluded: number; missingEntry: number;
  nodes: (JourneyDefinition["nodes"][number] & { players: number; returned?: number; mature?: number; matureReturned?: number })[];
  branches: { id: string; name: string; choice: string | null; players: number; completed: number; mature: number; matureCompleted: number; changedPath: number; nodes: { id: string; players: number }[] }[];
}

export interface JourneySelection {
  env:string; at:string; populationHash:string; definitionHash:string; basis:"journey"|"day-zero"; hours:number; build?:string; from?:string; to?:string;
}
export interface JourneyOutcomes {
  selection:JourneySelection;
  nodes:{id:string;noNext:number;timeRemaining:number;sessionEnded:number;unclear:number}[];
}
export interface JourneyNodeFunnel {
  selection:JourneySelection;node:string;funnel:string;players:number;steps:{step:number;name:string;players:number}[];
}

export interface GameMetadata extends DisplayEntry {
  icon?: string;
  universeId?: string;
  environments: Record<string, DisplayEntry>;
  currencies: Record<string, CurrencyMetadata>;
  products: Record<string, DisplayEntry>;
  items: Record<string, DisplayEntry>;
  skills: Record<string, DisplayEntry>;
  rewards: Record<string, DisplayEntry>;
  events: Record<string, EventMetadata>;
  funnels: Record<string, FunnelMetadata>;
  experiments: Record<string, DisplayEntry>;
  annotations: { at: string; label: string; kind: "release" | "event" }[];
}

export interface PublicProductConfig {
  schemaVersion: 1;
  product: {
    name: string;
    tagline: string;
    icon: string;
    lockup: string;
    signInBackground?: string;
    documentationUrl: string;
    supportUrl: string;
    theme?: {
      action?: string;
      series1?: string;
      series2?: string;
      series3?: string;
    };
  };
  auth: {
    provider: "google";
    hostedDomain: string;
    googleClientId: string;
  };
}

export interface ProductConfig extends PublicProductConfig {
  games: Record<string, GameMetadata>;
}

export interface Meta {
  tier: "admin" | "team";
  /** Newest event the pipeline holds for this scope. `null` = no data. */
  freshAsOf: string | null;
  computedAt: string;
  cached: boolean;
  refreshState?: "refreshing" | "failed";
  /** Correlates a UI failure or answer with API logs; never a credential. */
  requestId?: string;
  /** Public request-stage duration reported by the API. */
  serverTiming?: string;
  queryMs?: number;
}

export interface Envelope<T> {
  meta: Meta;
  data: T;
}

export interface GameSummary {
  gameId: string;
  environments: string[];
  players: number;
  sessions: number;
  medianSessionSeconds: number | null;
  bounce: number | null;
  lastSeen: string;
  liveCcu: number;
  daily: { day: string; players: number }[];
}

export interface Overview {
  retention: { d1: number | null; d7: number | null; d30: number | null };
  retentionDates: { d1: string | null; d7: string | null; d30: string | null };
  audience: { day: string; newPlayers: number; newShare: number | null } | null;
  serverFps: number | null;
  shopDrop: { from: string; to: string; rate: number } | null;
  economy?: { robux: number; payers: number; players: number };
}

export interface Pulse {
  gameId: string;
  env: string;
  windowHours: number;
  /** `ccu` is the PEAK of the window's two minutes — a whole number of
   *  people. `meanCcu` is their average, which is what tracks churn. */
  ccu: { at: string; ccu: number; meanCcu: number | null }[];
  players: number;
  sessions: {
    at: string;
    sessions: number;
    newPlayerSessions: number;
    newPlayerShare: number | null;
  }[];
  sessionShape: {
    engagedSessions: number;
    stubUnder30s: number;
    p25Seconds: number | null;
    medianSeconds: number | null;
    p75Seconds: number | null;
    meanSeconds: number | null;
  };
  pipelineLagSeconds: number | null;
}

export type OverviewSection = "activity" | "sessions" | "retention" | "economy" | "discovery";
export interface ReportPeriod {
  from: string; to: string; days: number; reportedDays: number; complete: boolean;
  totals?: { players?: number; player_days?: number; new_players?: number; returning_players?: number; returning_days?: number; unknown_days?: number;
    playtime_seconds?: number; visits?: number; sessions?: number; engaged?: number; seconds?: number; early_exits?: number;
    mean_seconds?: number | null; robux?: number; payers?: number; purchases?: number } | null;
  horizons?: Record<"d1" | "d7" | "d30", { value: number | null; complete: boolean; players: number; reportedDays: number; days: number }>;
  series: { day?: string; cohort?: string; bucket_ms?: number; players?: number; new_players?: number; playtime_seconds?: number;
    player_days?: number; mean_seconds?: number | null; sessions?: number; robux?: number; payers?: number; d1?: number | null; qualified_players?: number | null; qualified_rate?: number | null }[];
}
export interface OverviewReport { section: OverviewSection; current: ReportPeriod; previous: ReportPeriod | null; comparisonFailed: boolean }

/** Every build seen in the window, newest release first. */
export interface Builds {
  gameId: string;
  env: string;
  windowHours: number;
  builds: {
    build: string;
    /** The most recently released build in this window. */
    latest: boolean;
    /**
     * The build that sent something most recently — what is actually running.
     * Usually the same as `latest`; when it is NOT, a release was rolled back
     * or never took, and that gap is the thing worth seeing.
     */
    current: boolean;
    firstSeen: string;
    lastSeen: string;
    players: number;
    sessions: number;
    servers: number;
  }[];
}

/** The product screen: one request, because the panels share a window. */
export interface Product {
  gameId: string;
  env: string;
  windowHours: number;
  /**
   * What each panel needs and this game does not send. The difference between
   * "never instrumented" and "quiet window" is invisible on screen unless the
   * screen is told, and the two mean opposite things.
   */
  instrumentation: { panel: string; missing: string[] }[];
  events: { event: string; rows: number; users: number; lastSeen: string }[];
  chain: {
    step: number;
    stepName: string;
    users: number;
    /** Logged at exactly this step, before the monotonic inference. */
    recorded: number;
  }[];
  blocked: {
    itemSku: string;
    currency: string;
    attempts: number;
    users: number;
    medianShortfall: number | null;
    medianPrice: number | null;
  }[];
  faucet: {
    source: string;
    kind: string;
    grants: number;
    items: number;
    users: number;
  }[];
  catalogue: {
    itemSku: string;
    kind: string;
    unlockedBy: number;
    equippedBy: number;
    equipRate: number | null;
  }[];
  skills: {
    itemSku: string;
    uses: number;
    kills: number;
    damage: number;
    users: number;
    damagePerUse: number | null;
    killsPerUse: number | null;
  }[];
}

export interface Live {
  total: number;
  games: { gameId: string; ccu: number }[];
}

export interface ExitRatePoint {
  at: string;
  allSessions: number | null;
  newPlayers: number | null;
  newSessions: number;
  /** The trailing window's session count — what the rate divides by. */
  sessions: number;
}

export interface SessionsScreen {
  measurement: {
    basis: "completed-by-end-time";
    unfinished: number;
    estimated: number;
    calculatedAt: string | null;
    selectedFrom: string | null;
    selectedTo: string | null;
    completedBefore: string | null;
  };
  gameId: string;
  env: string;
  windowHours: number;
  bucketMinutes: number;
  length: {
    at: string;
    p25Seconds: number | null;
    medianSeconds: number | null;
    p75Seconds: number | null;
    /** Roblox's basis, drawn beside the median. The gap is the skew. */
    meanSeconds: number | null;
    sessions: number;
  }[];
  /**
   * How many minutes each bounce point is measured over. Equal to the bucket
   * at coarse windows; wider than it at fine ones, so a one-minute step still
   * divides by something. The panel says so rather than letting the reader
   * assume the two match.
   */
  rateWindowMinutes: number;
  bounce: ExitRatePoint[];
  /** The same series at three minutes: finished sessions under 180 seconds.
   * Optional until every API revision serving the page sends it. */
  under180?: ExitRatePoint[];
  /** Every bucket, including the empty ones — a histogram with gaps closed up
   * misreports the shape of the distribution. */
  histogram: {
    label: string;
    upperSeconds: number | null;
    sessions: number;
    isStub: boolean;
  }[];
  shape: {
    totalSessions: number;
    under60s: number;
    under180s?: number;
    engagedSessions: number;
    stubUnder30s: number;
    p25Seconds: number | null;
    medianSeconds: number | null;
    p75Seconds: number | null;
    meanSeconds: number | null;
  };
  /** Tracking-only measurement diagnostics. The Sessions screen opts out. */
  quality: {
    source: string;
    sessions: number;
    meanSeconds: number | null;
    medianSeconds: number | null;
    multiPlace: number;
    meanPlaces: number | null;
  }[];
}

export interface EngagementDaily {
  from: string | null;
  to: string | null;
  calculatedAt: string | null;
  summary: { playerDays: number; sessions: number; seconds: number; meanSeconds: number | null; sessionsPerPlayer: number | null };
  days: (EngagementDaily["summary"] & { day: string })[];
}

export interface Retention {
  gameId: string;
  env: string;
  windowHours: number;
  /** `null` at a horizon the cohort has not reached yet — never 0. */
  cohorts: {
    cohort: string;
    size: number;
    d1: number | null;
    d7: number | null;
    d30: number | null;
  }[];
  scale: {
    mau: number;
    dau: number;
    stickiness: number | null;
    sessionsPerPlayer: number | null;
    /** The MEAN, on Roblox's basis. Labelled as such wherever it is shown. */
    avgSessionSeconds: number | null;
    /**
     * Share of visits whose first-seen date came from the player's own
     * document rather than being assumed. Zero means cohorts cannot be formed
     * for this game at all; it is what lets the empty grid explain itself
     * instead of blaming an integration that may be fine.
     */
    exactShare: number | null;
  };
  playtime: {
    day: string;
    medianSeconds: number | null;
    p90Seconds: number | null;
    players: number;
  }[];
}

export interface RetentionReport extends Pick<Retention, "gameId" | "env" | "windowHours" | "cohorts"> {
  returnDays: { cohort: string; day: number; players: number; returned: number | null }[];
  processing: { state: string; failed: boolean; lastSuccessStartedAt: string | null;
    lastSuccessFinishedAt: string | null; nextRefreshAt: string | null; checkedAt: string } | null;
}

/**
 * One persisted series imported from Roblox Analytics. These rows stay
 * aggregate-only: a bucket may describe a platform, country, or acquisition
 * source, but it is never joined back to an individual Neulytics player.
 */
export interface RobloxMetricInfo {
  metric: string;
  label: string;
  family: string;
  unit?: "ratio" | "count" | "minutes" | "text";
  description?: string;
  dimensions: string[];
  granularities: string[];
}
export interface RobloxCapabilities {
  robloxMetrics: RobloxMetricInfo[];
  breakdowns: { key: string; label: string; robloxDimension?: string }[];
}
export interface RobloxMetricSeries {
  pending?: boolean;
  warning?: string;
  source?: "stored" | "roblox" | "unavailable";
  gameId: string;
  metric: {
    metric: string;
    label: string;
  };
  breakdown: {
    key: string;
    label: string;
  } | null;
  granularity: string;
  days: number;
  rows: {
    bucket_start: string;
    bucket: string;
    raw_bucket: string;
    value: number | null;
    string_values: string[];
    status: string;
    fetched_at_ms: number;
  }[];
}

export interface FunnelList {
  gameId: string;
  funnels: { funnel: string; steps: number; entries: number }[];
}

/**
 * WHAT WE OBSERVED EACH PLAYER RECEIVE — never who Roblox enrolled.
 *
 * ConfigSnapshot:GetValue() returns a value and nothing else. Roblox enrols on
 * a rollout percentage and hands the control value to everyone it excluded, so
 * below a 100% rollout "saw the control value" and "is in the control arm" are
 * different populations, and only the first is visible to us. The assignment,
 * the ramp, and the verdict on D1, playtime and revenue all stay theirs.
 */
export interface ExperimentList {
  gameId: string;
  env: string;
  experiments: {
    experiment: string;
    firstSeen: string;
    lastSeen: string;
    players: number;
    groups: { value: string; players: number }[];
    /**
     * Players who saw more than one value for this key. Reported, never used
     * to refuse the key: ramping a rollout moves players between values on
     * purpose. They are excluded from the comparison because their outcome is
     * not attributable to one value.
     */
    changedPlayers: number;
  }[];
}

export interface RegisteredExperiment {
  schemaVersion: 1; id: string; version: number; name: string;
  eligibility: { id: string; description: string }; startsAt: string; endsAt: string;
  variants: { id: string; name: string; percent: number }[];
}
export interface ExperimentAllocation {
  definition: RegisteredExperiment;
  status: "scheduled" | "not-started" | "running" | "ended";
  assignedPlayers: number; recordedPlayers: number; changedPlayers: number; invalidPlayers: number;
  groups: { id: string; name: string; percent: number; players: number; expectedPlayers: number; actualPercent: number | null; differencePoints: number | null }[];
  splitCheck: "tracking-issue" | "unexpected" | "waiting" | "no-warning";
  tolerancePoints: number | null;
}

export interface Interval {
  low: number;
  high: number;
}

export interface GroupRate {
  value: string;
  players: number;
  reached: number;
  rate: number;
  interval: Interval;
  /** Absent on the baseline itself, and on every group when none is chosen. */
  versusControl?: {
    absolute: number;
    absoluteInterval: Interval;
    relative: number | null;
    verdict: "winning" | "losing" | "not-enough-evidence";
  };
}

export interface ExperimentDetail {
  gameId: string;
  env: string;
  experiment: string;
  funnel: string;
  windowHours: number;
  sinceDays: number;
  /** Null until the reader picks one. The API will not guess a baseline. */
  control: string | null;
  groups: { value: string; players: number }[];
  /**
   * Playtime per player. For a real Roblox experiment their own playtime-by-arm
   * is the better number, computed on their population; this is for a rollout
   * the game runs, which Roblox has never heard of.
   */
  engagement: {
    value: string;
    players: number;
    meanSeconds: number;
    medianSeconds: number;
  }[];
  builds: { value: string; build: string; players: number }[];
  steps: { step: number; name?: string; groups: GroupRate[] }[];
}

export interface FunnelUnitCounts {
  reached: number;
  ofEntry: number | null;
  ofPrevious: number | null;
}

export interface FunnelDetail {
  journey?: {
    countingUnit: "people";
    buildBasis: "entry";
    entrants: number;
    completed: number;
    completionRate: number | null;
    terminalStep: number;
    terminalName: string;
    mixedBuildPlayers: number;
    unmappedPlayers: number;
    compatibleFunnels: string[];
  };
  gameId: string;
  funnel: string;
  windowHours: number;
  /**
   * Everyone who played in this window, funnel or not. A funnel's first step
   * is not the top of anything: 96% step-to-step conversion looks excellent
   * and says nothing until you know how few people reached step one.
   */
  players: number;
  /**
   * Of the entrants, how many hit a balance wall AFTER entering. The
   * difference between "they did not want it" and "they could not pay for
   * it" — two explanations for the same drop with nothing in common.
   */
  wall: {
    entrants: number;
    blockedUsers: number;
    attempts: number;
    medianShortfall: number | null;
  };
  days: number;
  steps: {
    step: number;
    name: string;
    users: FunnelUnitCounts;
    attempts: FunnelUnitCounts;
    /**
     * The un-inferred count — units that logged THIS step. Below `reached`
     * means some passed it without it being recorded, which is a missing
     * instrumentation call far more often than it is player behaviour.
     */
    recorded: { users: number; attempts: number };
    recovered?: { users: number; attempts: number };
  }[];
  completion: {
    day: string;
    users: number;
    attempts: number;
    enteredUsers: number;
  }[];
}

export interface Performance {
  gameId: string;
  env: string;
  windowHours: number;
  bucketMinutes: number;
  serverFps: { at: string; build: string; fps: number | null }[];
  serverMemory: {
    at: string;
    avgMb: number | null;
    worstMb: number | null;
    servers: number;
    playersPerServer: number | null;
  }[];
  client: {
    at: string;
    device: string;
    fps: number | null;
    pingMs: number | null;
    samples: number;
  }[];
  /** Zero means "no sampler wired" — a different screen from "no players". */
  serverSamples: number;
  clientSamples: number;
}

export interface AudienceReport {
  gameId: string; env: string; dates: { from: string; to: string };
  rows: { day: string; players: number; new_players: number; known_returning: number;
    unknown_players: number; away30: number; away60: number; away90: number }[];
}
export interface AudienceActives {
  gameId: string; env: string; dates: { from: string; to: string };
  rows: { day: string; dau: number; wau: number | null; mau: number | null; first_day: string }[];
}
export interface Audience {
  gameId: string;
  env: string;
  windowHours: number;
  daily: {
    day: string;
    players: number;
    newPlayers: number;
    returningPlayers: number;
    newShare: number | null;
    sessions: number;
  }[];
  /** Where "gone" begins, in days. Echoed back so the picker can show it. */
  resurrectDays: number;
  /**
   * WHO SHOWED UP, IN FOUR BUCKETS THAT SUM TO `players`.
   *
   * `unknownPlayers` is not a rounding error and is currently the largest of
   * them. A player we have no previous visit for, whose game never told us
   * they were new, is genuinely unclassifiable — and calling that "returning"
   * (which the old two-way split did) reports total loyalty for a game that
   * might be nothing but first-timers.
   *
   * It shrinks on its own: a 30-day absence cannot be seen in a store twelve
   * days deep, and every day of history makes more of it visible.
   */
  lifecycle: {
    day: string;
    players: number;
    newPlayers: number;
    returningPlayers: number;
    resurrectedPlayers: number;
    unknownPlayers: number;
  }[];
  /** D0..D30. `cohortSize` shrinks as the offset grows — read them together. */
  curve: {
    dayOffset: number;
    cohortSize: number;
    retained: number;
    rate: number | null;
  }[];
  stickiness: {
    day: string;
    dau: number;
    wau: number;
    mau: number;
    dauOverMau: number | null;
    dauOverWau: number | null;
  }[];
  places: {
    place: string;
    players: number;
    sessions: number;
    playerMinutes: number;
  }[];
  /** Empty until a telemetry package emits props['device']. None does yet. */
  devices: { device: string; players: number; sessions: number }[];
}

export interface Economy {
  gameId: string;
  windowHours: number;
  bucketMinutes: number;
  /** Absent on older API revisions. robux has always been receipt spending. */
  robuxBasis?: "player_spending_before_fees";
  /** Player spending before Roblox fees over the selected window, not earned revenue. */
  robux: number;
  payers: number;
  purchases: number;
  /**
   * PLAYERS PRESENT IN THE WINDOW, never DAU — there is no DAU of forty
   * minutes, and a second definition of DAU beside Audience's is exactly the
   * thing this product went to some trouble to remove.
   */
  players: number;
  series: {
    at: string;
    robux: number;
    payers: number;
    purchases: number;
    players: number;
    conversion: number | null;
    arppu: number | null;
  }[];
  products: {
    product: string;
    robux: number;
    payers: number;
    purchases: number;
  }[];
}

export interface Currency {
  gameId: string;
  windowHours: number;
  bucketMinutes: number;
  flows: {
    at: string;
    direction: string;
    currency: string;
    source: string;
    total: number;
    users: number;
  }[];
}

export interface ParityRow {
  key: string;
  label: string;
  agreement: "aligned" | "divergent";
  comparable?: boolean;
  /** Full definitions, both sides — a delta without them invites a "fix". */
  theirs: string;
  ours: string;
  isRate: boolean;
  tolerance: number;
  /** Set when our side cannot produce this metric yet. */
  unavailable: string | null;
  error: string | null;
  /** The date THEY last published. Their lag is per-metric. */
  comparedOn: string | null;
  lagDays: number | null;
  theirValue: number | null;
  ourValue: number | null;
  status: "match" | "drift" | "unknown";
  delta: number | null;
}

export interface Parity {
  pending?: boolean;
  partial?: boolean;
  gameId: string;
  configured: boolean;
  reason?: string;
  universeId?: string;
  metrics: ParityRow[];
  theirsOnly: { metric: string; why: string }[];
}

/**
 * WHAT TO DO WHEN THE SESSION HAS GONE.
 *
 * Sessions last twelve hours. When one expires, every request starts
 * returning 401 — and each screen was rendering that as "could not reach the
 * API", which is both false and a dead end: the API was answering fine, and
 * the reader had no way to recover except guessing to reload. Reported as
 * "the website is broken", correctly.
 *
 * So a 401 is handled ONCE, here, where every request already passes. The
 * session provider registers a handler that drops the app to the sign-in
 * screen, which is the only thing that actually fixes it.
 */
let onUnauthorized: (() => void) | null = null;
let lastRequestId: string | undefined;

export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

export function getLastRequestId(): string | undefined {
  return lastRequestId;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** "api" or "clickhouse" on a 403 — the two layers are told apart. */
    readonly refusedBy?: string,
    readonly requestId?: string,
  ) {
    super(message);
  }
}

function rethrowReadFailure(error: unknown): never {
  if (error instanceof DOMException && error.name === "TimeoutError") {
    // Apply to both headers and body reads. Retrying this deadline would
    // turn a ten-second bound into twenty seconds.
    throw new ApiError(
      504,
      "The analytics request took too long. Please try again.",
    );
  }
  throw error;
}

async function get<T>(
  path: string,
  signal?: AbortSignal,
): Promise<Envelope<T>> {
  const startedAt = performance.now();
  const response = await fetch(`${BASE}${path}`, {
    // The session cookie is httpOnly; nothing here can read it, which is the
    // point. `credentials` is what sends it, and why the API's CORS origin
    // list can never become a wildcard.
    credentials: "include",
    headers: { accept: "application/json" },
    signal: signal
      ? AbortSignal.any([signal, AbortSignal.timeout(10_000)])
      : AbortSignal.timeout(10_000),
  }).catch(rethrowReadFailure);
  lastRequestId = response.headers.get("x-request-id") ?? undefined;

  if (!response.ok) {
    if (response.status === 401) onUnauthorized?.();
    const body = (await response.json().catch(() => ({}))) as {
      error?: string;
      refusedBy?: string;
      requestId?: string;
    };
    throw new ApiError(
      response.status,
      body.error ?? `request failed (${response.status})`,
      body.refusedBy,
      body.requestId ?? response.headers.get("x-request-id") ?? undefined,
    );
  }
  const result = (await response
    .json()
    .catch(rethrowReadFailure)) as Envelope<T>;
  measure(`endpoint:${path.split("?")[0] ?? path}`, startedAt);
  result.meta.requestId = response.headers.get("x-request-id") ?? undefined;
  result.meta.serverTiming = response.headers.get("server-timing") ?? undefined;
  return result;
}

async function getBare<T>(path: string): Promise<T> {
  const startedAt = performance.now();
  const response = await fetch(`${BASE}${path}`, {
    credentials: "include",
    headers: { accept: "application/json" },
  });
  lastRequestId = response.headers.get("x-request-id") ?? undefined;
  if (!response.ok) {
    if (response.status === 401) onUnauthorized?.();
    const body = (await response.json().catch(() => ({}))) as {
      error?: string;
      requestId?: string;
    };
    throw new ApiError(
      response.status,
      body.error ?? `request failed (${response.status})`,
      undefined,
      body.requestId ?? response.headers.get("x-request-id") ?? undefined,
    );
  }
  const result = (await response.json()) as T;
  measure(`endpoint:${path}`, startedAt);
  return result;
}

/**
 * Everything about a request that is the READER's choice rather than the
 * screen's: where the window ends, and which build to look at.
 *
 * One object rather than a growing tail of positional arguments, because the
 * previous shape was `(gameId, hours, to?)` and adding two more would have
 * given every one of a dozen call sites three optional strings in a row to get
 * in the wrong order.
 */
export interface Scope {
  /** Exact lower bound for Engagement; UTC, inclusive. */
  from?: string;
  /** Last UTC day included, `YYYY-MM-DD`. */
  to?: string;
  /**
   * Last INSTANT included, `YYYY-MM-DDTHH:MM` in UTC. Wins over `to`, which it
   * contains — the API reads the day off it rather than being sent both.
   */
  at?: string;
  /** One build, or every build when absent. */
  build?: string;
  /**
   * One kind of player, or every kind when absent.
   *
   * Rides on the scope rather than on each call's own arguments because it is
   * the reader's choice about a whole screen, exactly like the window and the
   * build. Entries that cannot classify a player refuse it with a 400 rather
   * than returning everybody's rows under a filtered label.
   */
  segment?: "new" | "returning" | "resurrected" | "unknown";
  /** Where "gone" begins, in days. Only meaningful alongside `segment`. */
  resurrectDays?: number;
}

/**
 * Omitted entirely when nothing is chosen, so the common request stays
 * byte-identical to what it was before any of this existed — which keeps this
 * client's cache keys, and the API's, from splitting in two.
 */
function scopeQuery(scope: Scope | undefined): string {
  if (scope === undefined) return "";
  const parts: string[] = [];
  if (scope.from !== undefined) parts.push(`from=${encodeURIComponent(scope.from)}`);
  if (scope.at !== undefined) parts.push(`at=${encodeURIComponent(scope.at)}`);
  else if (scope.to !== undefined)
    parts.push(`to=${encodeURIComponent(scope.to)}`);
  if (scope.build !== undefined)
    parts.push(`build=${encodeURIComponent(scope.build)}`);
  // The threshold goes only WITH a segment. `resurrected` is defined by it and
  // the other buckets move as it moves, so sending one without the other
  // labels a chart with a population it did not ask for.
  if (scope.segment !== undefined) {
    parts.push(`segment=${encodeURIComponent(scope.segment)}`);
    if (scope.resurrectDays !== undefined)
      parts.push(`resurrectDays=${scope.resurrectDays}`);
  }
  return parts.length === 0 ? "" : `&${parts.join("&")}`;
}

function cohortDateQuery(dates: { from: string; to: string } | undefined): string {
  return dates ? `&cohortFrom=${encodeURIComponent(dates.from)}&cohortTo=${encodeURIComponent(dates.to)}` : "";
}

export const api = {
  journeyOutcomes:(gameId:string,data:JourneyDetail,signal?:AbortSignal)=>get<JourneyOutcomes>(`/v1/games/${encodeURIComponent(gameId)}/journeys/${encodeURIComponent(data.definition.id)}/analysis?${new URLSearchParams({env:data.selection?.env??"Production",version:String(data.definition.version),kind:"outcomes",selection:JSON.stringify(data.selection)})}`,signal),
  journeyNodeFunnel:(gameId:string,data:JourneyDetail,node:string,funnel:string,signal?:AbortSignal)=>get<JourneyNodeFunnel>(`/v1/games/${encodeURIComponent(gameId)}/journeys/${encodeURIComponent(data.definition.id)}/analysis?${new URLSearchParams({env:data.selection?.env??"Production",version:String(data.definition.version),kind:"funnel",node,funnel,selection:JSON.stringify(data.selection)})}`,signal),
  notifications: (gameId: string, hours: number, env: string, campaign: string, scope?: Scope, signal?: AbortSignal) =>
    get<NotificationReport>(`/v1/games/${encodeURIComponent(gameId)}/notifications?hours=${hours}&env=${encodeURIComponent(env)}${campaign ? `&campaign=${encodeURIComponent(campaign)}` : ""}${scopeQuery(scope)}`, signal),
  productDashboards: (gameId: string, signal?: AbortSignal) => get<{ title: string; source: string | null; labels?: Record<string, string>; panels: ProductDashboardPanel[] }>(`/v1/games/${encodeURIComponent(gameId)}/product-dashboards`, signal),
  productDashboard: (gameId: string, panel: string, hours: number, scope?: Scope, signal?: AbortSignal) =>
    get<{ rows: ProductDashboardRow[]; missing: string[]; limited: boolean; emptyReason?: "missing-events" | "no-matching-results" | null }>(`/v1/games/${encodeURIComponent(gameId)}/product-dashboards/${encodeURIComponent(panel)}?hours=${hours}${scopeQuery(scope)}`, signal),
  journeyRetention: (gameId: string, journey: string, version: number, dates: { from: string; to: string }, signal?: AbortSignal) =>
    get<JourneyDetail>(`/v1/games/${encodeURIComponent(gameId)}/journeys/${encodeURIComponent(journey)}/retention?${new URLSearchParams({ version: String(version), ...dates })}`, signal),
  journeys: (gameId: string, signal?: AbortSignal) =>
    get<{ journeys: JourneyRegistration[] }>(`/v1/games/${encodeURIComponent(gameId)}/journeys`, signal),
  journey: (gameId: string, journey: string, version: number, hours: number, scope?: Scope, signal?: AbortSignal) =>
    get<JourneyDetail>(`/v1/games/${encodeURIComponent(gameId)}/journeys/${encodeURIComponent(journey)}?version=${version}&hours=${hours}${scopeQuery(scope)}`, signal),
  configuration: () => getBare<PublicProductConfig>("/v1/config"),
  metadata: () => getBare<ProductConfig>("/v1/metadata"),
  async signIn(
    credential: string,
  ): Promise<{ email: string; role: "admin" | "team" }> {
    const response = await fetch(`${BASE}/v1/auth/google`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ credential }),
    });
    if (!response.ok) throw new ApiError(response.status, "sign-in refused");
    return (await response.json()) as { email: string; role: "admin" | "team" };
  },

  async signOut(): Promise<void> {
    await fetch(`${BASE}/v1/auth/logout`, {
      method: "POST",
      credentials: "include",
    });
  },

  me: (signal?: AbortSignal) =>
    get<{ email: string; role: "admin" | "team" }>("/v1/me", signal),
  games: () => get<{ games: GameSummary[] }>("/v1/games"),
  live: (signal?: AbortSignal) => get<Live>("/v1/live", signal),
  overview: (gameId: string, signal?: AbortSignal) =>
    get<Overview>(`/v1/games/${encodeURIComponent(gameId)}/overview`, signal),
  overviewReport: (gameId: string, section: OverviewSection, dates: { from: string; to: string }, signal?: AbortSignal) =>
    get<OverviewReport>(`/v1/games/${encodeURIComponent(gameId)}/overview-report/${section}?${new URLSearchParams(dates)}`, signal),
  ccu: (gameId: string, hours: number, signal?: AbortSignal) =>
    get<{ windowHours: number; bucketMinutes: number; ccu: Pulse["ccu"] }>(
      `/v1/games/${encodeURIComponent(gameId)}/ccu?hours=${hours}`,
      signal,
    ),
  // Average players online per interval by type; the four stack to the average total.
  ccuTypes: (gameId: string, hours: number, signal?: AbortSignal) =>
    get<{ windowHours: number; bucketMinutes: number; points: CcuTypePoint[] }>(
      `/v1/games/${encodeURIComponent(gameId)}/ccu/types?hours=${hours}`,
      signal,
    ),
  pulse: (
    gameId: string,
    hours: number,
    scope?: Scope,
    signal?: AbortSignal,
    includeCcu = true,
  ) =>
    get<Pulse>(
      `/v1/games/${encodeURIComponent(gameId)}/pulse?hours=${hours}${scopeQuery(scope)}${includeCcu ? "" : "&includeCcu=false"}`,
      signal,
    ),
  exitsByDevice: (gameId: string, hours: number, scope?: Scope, signal?: AbortSignal) =>
    get<DeviceExits>(`/v1/games/${encodeURIComponent(gameId)}/sessions/exits-by-device?hours=${hours}${scopeQuery(scope)}`, signal),
  sessions: (
    gameId: string,
    hours: number,
    scope?: Scope,
    includeQuality = true,
    signal?: AbortSignal,
  ) =>
    get<SessionsScreen>(
      `/v1/games/${encodeURIComponent(gameId)}/sessions?hours=${hours}${scopeQuery(scope)}` +
        (includeQuality ? "" : "&includeQuality=false"),
      signal,
    ),
  engagementDaily: (gameId: string, hours: number, scope?: Scope, signal?: AbortSignal) =>
    get<EngagementDaily>(`/v1/games/${encodeURIComponent(gameId)}/sessions/daily?hours=${hours}${scopeQuery(scope)}`, signal),
  retention: (
    gameId: string,
    hours: number,
    scope?: Scope,
    signal?: AbortSignal,
    dates?: { from: string; to: string },
  ) =>
    get<Retention>(
      `/v1/games/${encodeURIComponent(gameId)}/retention?hours=${hours}${scopeQuery(scope)}` + cohortDateQuery(dates),
      signal,
    ),
  retentionCohorts: (gameId: string, hours: number, dates: { from: string; to: string }, signal?: AbortSignal) =>
    get<RetentionReport>(
      `/v1/games/${encodeURIComponent(gameId)}/retention?view=report&hours=${hours}${cohortDateQuery(dates)}`, signal),
  robloxMetricSeries: (
    gameId: string,
    metric: string,
    days: number,
    breakdown?: string,
    dates?: { from: string; to: string },
    signal?: AbortSignal,
    granularity: "OneDay" | "OneHour" = "OneDay",
  ) =>
    get<RobloxMetricSeries>(
      `/v1/games/${encodeURIComponent(gameId)}/breakdowns/roblox/${encodeURIComponent(metric)}` +
        `?granularity=${granularity}&days=${days}` +
        (breakdown === undefined
          ? ""
          : `&breakdown=${encodeURIComponent(breakdown)}`) + cohortDateQuery(dates),
      signal,
    ),
  robloxCapabilities: (gameId: string, signal?: AbortSignal) => get<RobloxCapabilities>(`/v1/games/${encodeURIComponent(gameId)}/breakdowns`, signal),
  thumbnailPreviews: (gameId: string, assets: string[], signal?: AbortSignal) => get<{ images: Record<string, string | null> }>(`/v1/games/${encodeURIComponent(gameId)}/thumbnail-previews?assets=${assets.join(",")}`, signal),
  funnels: (
    gameId: string,
    hours: number,
    scope?: Scope,
    signal?: AbortSignal,
  ) =>
    get<FunnelList>(
      `/v1/games/${encodeURIComponent(gameId)}/funnels?hours=${hours}${scopeQuery(scope)}`,
      signal,
    ),
  funnel: (
    gameId: string,
    funnel: string,
    hours: number,
    scope?: Scope,
    signal?: AbortSignal,
  ) =>
    get<FunnelDetail>(
      `/v1/games/${encodeURIComponent(gameId)}/funnels/${encodeURIComponent(funnel)}?hours=${hours}${scopeQuery(scope)}`,
      signal,
    ),
  experiments: (gameId: string) =>
    get<ExperimentList>(`/v1/games/${encodeURIComponent(gameId)}/experiments`),
  registeredExperiments: (gameId: string, signal?: AbortSignal) =>
    get<{ experiments: { id: string; version: number; name: string; definitionHash: string; definition: RegisteredExperiment | null; problem: string | null }[] }>(`/v1/games/${encodeURIComponent(gameId)}/experiment-definitions`, signal),
  experimentAllocation: (gameId: string, experiment: string, version: number, signal?: AbortSignal) =>
    get<ExperimentAllocation>(`/v1/games/${encodeURIComponent(gameId)}/experiment-definitions/${encodeURIComponent(experiment)}/assignments?version=${version}`, signal),
  experiment: (
    gameId: string,
    experiment: string,
    funnel: string,
    hours: number,
    sinceDays: number,
    control: string,
  ) =>
    get<ExperimentDetail>(
      `/v1/games/${encodeURIComponent(gameId)}/experiments/${encodeURIComponent(experiment)}` +
        `?funnel=${encodeURIComponent(funnel)}&hours=${hours}` +
        `&sinceDays=${sinceDays}` +
        (control === "" ? "" : `&control=${encodeURIComponent(control)}`),
    ),
  product: (
    gameId: string,
    hours: number,
    scope?: Scope,
    signal?: AbortSignal,
  ) =>
    get<Product>(
      `/v1/games/${encodeURIComponent(gameId)}/product?hours=${hours}${scopeQuery(scope)}`,
      signal,
    ),
  // Product panels only need tracking presence. Data health continues to use
  // product() above because it actually displays event/player counts.
  productPanels: (
    gameId: string,
    hours: number,
    scope?: Scope,
    signal?: AbortSignal,
  ) =>
    get<Omit<Product, "events"> & {events: {event: string; rows: null; users: null; lastSeen: string}[]}>(
      `/v1/games/${encodeURIComponent(gameId)}/product?hours=${hours}&includeEventCounts=false${scopeQuery(scope)}`,
      signal,
    ),
  builds: (
    gameId: string,
    hours: number,
    scope?: Scope,
    signal?: AbortSignal,
  ) =>
    get<Builds>(
      `/v1/games/${encodeURIComponent(gameId)}/builds?hours=${hours}${scopeQuery(scope)}`,
      signal,
    ),
  parity: (gameId: string, days: number) =>
    get<Parity>(`/v1/games/${encodeURIComponent(gameId)}/parity?days=${days}`),
  performance: (
    gameId: string,
    hours: number,
    scope?: Scope,
    signal?: AbortSignal,
  ) =>
    get<Performance>(
      `/v1/games/${encodeURIComponent(gameId)}/performance?hours=${hours}${scopeQuery(scope)}`,
      signal,
    ),
  audienceReport: (gameId: string, dates: { from: string; to: string }, signal?: AbortSignal) =>
    get<AudienceReport>(`/v1/games/${encodeURIComponent(gameId)}/audience?view=report${cohortDateQuery(dates)}`, signal),
  audienceActives: (gameId: string, dates: { from: string; to: string }, signal?: AbortSignal) =>
    get<AudienceActives>(`/v1/games/${encodeURIComponent(gameId)}/audience?view=active${cohortDateQuery(dates)}`, signal),
  audience: (
    gameId: string,
    hours: number,
    scope?: Scope,
    // Where "gone" begins. Omitted means the API's default of 30 days, which
    // keeps every existing caller and saved link working unchanged.
    resurrectDays?: number,
    signal?: AbortSignal,
  ) =>
    get<Audience>(
      `/v1/games/${encodeURIComponent(gameId)}/audience?hours=${hours}${scopeQuery(scope)}` +
        (resurrectDays === undefined ? "" : `&resurrectDays=${resurrectDays}`),
      signal,
    ),
  audienceTrend: (gameId: string, hours: number, scope: Scope, resurrectDays: number, signal?: AbortSignal) =>
    get<Pick<Audience, "gameId" | "env" | "windowHours" | "daily" | "lifecycle" | "resurrectDays">>(
      `/v1/games/${encodeURIComponent(gameId)}/audience?view=trend&hours=${hours}${scopeQuery(scope)}&resurrectDays=${resurrectDays}`, signal),
  performanceTrend: (gameId: string, hours: number, scope: Scope, signal?: AbortSignal) =>
    get<Pick<Performance, "gameId" | "env" | "windowHours" | "bucketMinutes" | "serverFps" | "serverMemory" | "serverSamples">>(
      `/v1/games/${encodeURIComponent(gameId)}/performance?view=server&hours=${hours}${scopeQuery(scope)}`, signal),
  currency: (
    gameId: string,
    hours: number,
    scope?: Scope,
    grain: "auto" | "hour" | "day" = "auto",
    signal?: AbortSignal,
  ) =>
    get<Currency>(
      `/v1/games/${encodeURIComponent(gameId)}/currency?hours=${hours}&grain=${grain}${scopeQuery(scope)}`,
      signal,
    ),
  /** Admin tier. A team session gets a 403 carrying `refusedBy`. */
  economy: (
    gameId: string,
    hours: number,
    scope?: Scope,
    grain: "auto" | "hour" | "day" = "auto",
    signal?: AbortSignal,
  ) =>
    get<Economy>(
      `/v1/games/${encodeURIComponent(gameId)}/economy?hours=${hours}&grain=${grain}${scopeQuery(scope)}`,
      signal,
    ),
};
